import NiceModal from "@ebay/nice-modal-react";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import * as treeUtils from "utils/treeUtils";
import { NodeSchema, UpdateNodeSchema } from "interfaces/schemas";
import { useCopiedItemStore, useProjectDataStore } from "stores/projectStore";
import { useNodeMutations } from "hooks/projectHooks/useNodeMutations";
import { NodeKinds } from "interfaces/enums";
import { createNodesInput, getPath } from "components/ProjectTree/utils";
import { useGenNewFolderName } from "components/ProjectTree/hooks/useGenNewFolderName";
import {
  calculateNewNodePosition,
  calculatePartiallyVisible,
  computeNodeToggleActiveStatusUpdates,
  popNodeFromParent,
  updateAncestorsActiveStatus,
  updateSiblingsAfterInsert,
  updateSiblingsAfterPop,
} from "./utils";

export const useNodeActions = () => {
  const projectId = useProjectDataStore((state) => state.projectId);
  const setProjectTree = useProjectDataStore((state) => state.setProjectTree);
  const copiedItem = useCopiedItemStore((state) => state.copiedItem);
  const setCopiedItem = useCopiedItemStore((state) => state.setCopiedItem);
  const genNewFolderName = useGenNewFolderName();

  const nodeMutations = useNodeMutations();

  const moveNode = useCallback(
    ({ parentId, index, dragNodes, parentNode }, callback) =>
      setProjectTree((tree) => {
        // Extract the first node being dragged
        const dragNode = dragNodes[0];

        // Set the redraw index with the minimum of the dragged node's row index and the parent node's row index
        callback(Math.min(dragNode.rowIndex, parentNode.rowIndex));
        const { children: siblingNodes } = parentNode;

        // Count the number of product nodes among the siblings
        let firstValidPosition = 0;
        if (treeUtils.isFolder(dragNode.data)) {
          siblingNodes.forEach((node) => {
            if (
              treeUtils.isAnyProduct(node.data) ||
              treeUtils.isWizard(node.data) ||
              treeUtils.isTroxNetcom(node.data)
            ) {
              firstValidPosition = node.data.position + 1;
            }
          });
        }

        // Determine the new position for the node
        let newPos = index;
        const prevPos = dragNode.data.position;

        if (newPos < firstValidPosition) {
          newPos = siblingNodes.length;
        }

        // Check if the node is being moved within the same parent
        const isSameParent =
          parentId === dragNode.parent.id ||
          parentId === dragNode.data.parentId;

        // Adjust the new position if the node is moved within the same parent and the new position is after the previous position
        if (isSameParent && newPos > prevPos) {
          newPos -= 1;
        }

        const currentParent = dragNode.parent;

        const nodesToUpdate = new Map();

        const { node: removedNode, index: removedIndex } = popNodeFromParent(
          currentParent.data,
          dragNode.data.id,
        );

        removedNode.position = newPos;
        removedNode.parentId = treeUtils.validateNodeId(parentId, projectId);

        nodesToUpdate.set(removedNode.id, {
          id: removedNode.id,
          position: removedNode.position,
          parentId: treeUtils.validateNodeId(parentId, projectId),
          kind: removedNode.kind,
        });

        updateSiblingsAfterPop(
          currentParent.data.nodes,
          removedIndex,
          nodesToUpdate,
        );

        const oldParentPartiallyVisible = calculatePartiallyVisible(
          currentParent.data,
        );

        const oldAncestorsToUpdate = [];

        updateAncestorsActiveStatus(
          currentParent,
          oldAncestorsToUpdate,
          projectId,
          {
            prev: {
              active:
                treeUtils.isLeafNode(currentParent.data) &&
                currentParent.data.active,
              partiallyVisible: oldParentPartiallyVisible,
            },
          },
        );

        // Insert the removed node into the new parent at the new position

        parentNode.data.nodes.splice(newPos, 0, removedNode);

        const newAncestorsToUpdate = [];

        const newParentPartiallyVisible = calculatePartiallyVisible(
          parentNode.data,
        );

        updateAncestorsActiveStatus(
          parentNode,
          newAncestorsToUpdate,
          projectId,
          {
            prev: {
              active: removedNode.active,
              partiallyVisible: newParentPartiallyVisible,
            },
          },
        );

        updateSiblingsAfterInsert(parentNode.data.nodes, newPos, nodesToUpdate);

        // Execute the mutation to update the node positions in the backend
        const updateNodes = Array.from(nodesToUpdate.values());

        nodeMutations.processNodes([
          {
            action: "UPDATE",
            nodes: [...oldAncestorsToUpdate, ...updateNodes],
          },
          {
            action: "UPDATE",
            nodes: newAncestorsToUpdate,
          },
        ]);

        return [...tree];
      }),
    [nodeMutations, projectId, setProjectTree],
  );

  const openNodeDownloadModal = useCallback(
    (node) => {
      const path = getPath(node.parent);
      const modalSubtitle = `${node.data.folderName} ${path && `(${path})`}`;
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle,
        projectId,
        nodeId: node.data.id,
      });
    },
    [projectId],
  );

  const createNode = useCallback(
    (node) =>
      setProjectTree((tree) => {
        const { data: newFolderName, error } = genNewFolderName(
          node.data.folderName,
          node.data.nodes,
          true,
        );
        if (error) {
          toast.error(error.message);
          return tree;
        }

        const newNode = createNodesInput({
          data: {
            folderName: newFolderName,
            folderDescription: "",
            parentId: treeUtils.validateNodeId(node.id, projectId),
            kind: NodeKinds.FOLDER,
            position: Array.isArray(node.data.nodes) ? node.children.length : 1,
            active: true,
          },
        });

        if (newNode.error) {
          toast.error(newNode.error.message);
          return;
        }

        if (!Array.isArray(node.data.nodes)) {
          node.data.nodes = [];
        }

        node.data.nodes.push({
          ...newNode.subTree,
          nodes: [],
          partiallyVisible: false,
        });

        if (node.isClosed) {
          node.toggle();
        }

        const nodesToUpdate = [];

        const partiallyVisible = calculatePartiallyVisible(node.data);

        updateAncestorsActiveStatus(node, nodesToUpdate, projectId, {
          prev: { active: true, partiallyVisible },
        });

        nodeMutations.processNodes([
          {
            action: "CREATE",
            nodes: newNode.nodesToRequest,
          },
          {
            action: "UPDATE",
            nodes: nodesToUpdate,
          },
        ]);

        return [...tree];
      }),
    [genNewFolderName, nodeMutations, projectId, setProjectTree],
  );

  // DELETE NODE
  const deleteNode = useCallback(
    (node) =>
      setProjectTree((tree) => {
        const currentParent = node.parent;

        const nodesToUpdate = new Map();
        const { node: removedNode, index: removedIndex } = popNodeFromParent(
          currentParent.data,
          node.data.id,
        );

        updateSiblingsAfterPop(
          currentParent.data.nodes,
          removedIndex,
          nodesToUpdate,
        );

        const oldParentPartiallyVisible = calculatePartiallyVisible(
          currentParent.data,
        );

        const oldAncestorsToUpdate = [];

        updateAncestorsActiveStatus(
          currentParent,
          oldAncestorsToUpdate,
          projectId,
          {
            prev: {
              active:
                treeUtils.isLeafNode(currentParent.data) &&
                currentParent.data.active,
              partiallyVisible: oldParentPartiallyVisible,
            },
          },
        );

        const isWizardRoot = treeUtils.isWizardRootFolder(node.data);

        const updateNodes = Array.from(nodesToUpdate.values());

        const {
          nodes: _nodes,
          partiallyVisible: _partiallyVisible,
          ...nodeToDelete
        } = removedNode;

        nodeMutations.processNodes([
          {
            action: "DELETE",
            nodes: isWizardRoot ? [] : [nodeToDelete],
          },
          {
            action: "UPDATE",
            nodes: [...oldAncestorsToUpdate, ...updateNodes],
          },
        ]);

        if (isWizardRoot) {
          nodeMutations.deleteWizard(removedNode.id);
        }

        return [...tree];
      }),
    [nodeMutations, projectId, setProjectTree],
  );

  // UPDATE NODE
  const updateNode = useCallback(
    (node, args) => {
      const result = UpdateNodeSchema.safeParse({
        id: node.id,
        kind: node.data.kind,
        ...args,
      });
      if (!result.success) {
        toast.error(result.error.errors[0].message);
        return;
      }
      const updatedChildren = [];
      setProjectTree((prev) => {
        const newTree = [...prev];
        const currentNode = treeUtils.findNodeById({
          nodes: newTree,
          id: node.id,
        });

        if (result.data) {
          const oldQuantity = currentNode.quantity;
          const newQuantity = result.data.quantity;
          Object.keys(result.data).forEach((key) => {
            currentNode[key] = result.data[key];
          });
          if (
            currentNode.kind === NodeKinds.PRODUCT &&
            "quantity" in args &&
            oldQuantity !== newQuantity &&
            currentNode.nodes &&
            currentNode.nodes.length > 0
          ) {
            const quantityRatio = newQuantity / oldQuantity;
            currentNode.nodes.forEach((child) => {
              if (child.kind === NodeKinds.PRODUCT_ACCESSORY) {
                child.quantity = Math.round(child.quantity * quantityRatio);
                updatedChildren.push({
                  id: child.id,
                  quantity: child.quantity,
                  kind: child.kind,
                });
              }
            });
          }
        }
        return newTree;
      });

      const nodesToUpdate = [
        ...updatedChildren,
        {
          ...result.data,
          id: treeUtils.validateNodeId(result.data.id, projectId),
        },
      ];

      nodeMutations.processNodes([
        {
          action: "UPDATE",
          nodes: nodesToUpdate,
        },
      ]);
    },
    [nodeMutations, projectId, setProjectTree],
  );

  // UPDATE PRODUCT NODE CONFIGURATION
  const updateProductNodeConfiguration = useCallback(
    (nodeId, configData, callback) => {
      setProjectTree((prev) => {
        const newTree = [...prev];
        // node
        const nodeData = treeUtils.findNodeById({
          nodes: newTree,
          id: nodeId,
        });

        if (!nodeData) {
          toast.error("Node not found");
          console.error("Node not found");
          return prev;
        }

        if (
          !treeUtils.isProduct(nodeData) &&
          !treeUtils.isProductAccessory(nodeData)
        ) {
          toast.error("Node is not a product");
          console.error("Node is not a product");
          return prev;
        }

        const hasAccessories = configData.selectedAccessories.length > 0;

        const newNodeState = createNodesInput({
          data: {
            ...nodeData,
            orderCode: configData.orderCode,
            pimId: configData.pimId,
            presentation: configData.presentation,
            productName: configData.productName,
            nodes: hasAccessories
              ? configData.selectedAccessories.map((accessory, index) => ({
                  accessoryGroupId: accessory.accessoryGroupId,
                  accessoryId: accessory.accessoryId,
                  id: accessory.id,
                  parentId: nodeData.id,
                  kind: NodeKinds.PRODUCT_ACCESSORY,
                  position: index,
                  active: nodeData.active,
                  productName: accessory.title,
                  presentation: accessory.currentPresentation,
                  pimId: accessory.pimId,
                  quantity: nodeData.quantity,
                  orderCode: {
                    state: {
                      optionStates: accessory.optionStates,
                    },
                    seriesId: accessory.seriesId,
                    currentStrategyId: accessory.currentStrategyId,
                  },
                  nodes: null,
                }))
              : null,
            selectedAccessories: hasAccessories
              ? configData.selectedAccessories.map((accessory) => ({
                  accessoryGroupId: accessory.accessoryGroupId,
                  accessoryId: accessory.accessoryId,
                  currentPresentation: accessory.currentPresentation,
                  optionStates: accessory.optionStates,
                  seriesId: accessory.seriesId,
                }))
              : null,
          },
          callback: (node) => {
            if (node.parentId === nodeData.parentId) {
              return {
                ...node,
                id: nodeData.id,
              };
            }
            return node;
          },
        });

        if (newNodeState.error) {
          toast.error(newNodeState.error.message);
          console.error(newNodeState.error.message);
          return prev;
        }

        nodeMutations.processNodes([
          {
            action: "DELETE",
            nodes: [{ id: nodeData.id }],
          },
          {
            action: "CREATE",
            nodes: newNodeState.nodesToRequest,
          },
        ]);

        for (const key in nodeData) {
          if (Object.hasOwn(newNodeState.subTree, key)) {
            nodeData[key] = newNodeState.subTree[key];
          }
        }

        if (callback && typeof callback === "function") {
          callback(nodeData);
        }

        return newTree;
      });
    },
    [nodeMutations, setProjectTree],
  );

  // COPY NODE
  const copyNode = useCallback((node) => setCopiedItem(node), [setCopiedItem]);

  // PASTE NODE
  const pasteNode = useCallback(
    (node, cb) =>
      setProjectTree((tree) => {
        if (!copiedItem || !projectId) {
          return tree;
        }

        const { data } = copiedItem;

        const folderData = {};
        if (data?.folderName) {
          const { data: newFolderName, error } = genNewFolderName(
            data.folderName,
            node.data.nodes,
            false,
          );
          if (error) {
            toast.error(error.message);
            cb?.({ error });
            return tree;
          }
          folderData.folderName = newFolderName;
        }

        const newNode = createNodesInput({
          data: {
            ...data,
            ...folderData,
            parentId: treeUtils.validateNodeId(node.id, projectId),
            position: calculateNewNodePosition(data, node.data),
          },
          callback: (nodeData) => {
            if (
              treeUtils.isWizard(nodeData) ||
              treeUtils.isTroxNetcom(nodeData)
            ) {
              const isAnyProduct = treeUtils.isAnyProduct(nodeData);
              const isTroxNetcom = treeUtils.isTroxNetcom(nodeData);
              let kind;
              if (isAnyProduct) {
                kind = treeUtils.isAnyAccessory(nodeData)
                  ? NodeKinds.PRODUCT_ACCESSORY
                  : NodeKinds.PRODUCT;
              } else {
                kind = NodeKinds.FOLDER;
              }
              if (isTroxNetcom && isAnyProduct) {
                kind = NodeKinds.CUSTOM_PRODUCT;
              }
              // revalidate the node data with the correct kind to ensure it meets the schema requirements for the new kind
              const parsedNodeData = NodeSchema.safeParse({
                ...nodeData,
                kind,
              });
              if (!parsedNodeData.success) {
                toast.error(parsedNodeData.error.errors[0].message);
                console.error(parsedNodeData.error);
                throw new Error("failed to duplicate node");
              }
              return parsedNodeData.data;
            }
            return nodeData;
          },
        });

        if (newNode.error) {
          toast.error(newNode.error.message);
          cb?.(newNode);
          return tree;
        }

        if (!Array.isArray(node.data.nodes)) {
          node.data.nodes = [];
        }

        node.data.nodes.splice(newNode.subTree.position, 0, newNode.subTree);

        const newAncestorsToUpdate = [];

        const newParentPartiallyVisible = calculatePartiallyVisible(
          node.data.data,
        );

        updateAncestorsActiveStatus(node, newAncestorsToUpdate, projectId, {
          prev: {
            active: newNode.active,
            partiallyVisible: newParentPartiallyVisible,
          },
        });

        const nodesToUpdate = new Map();

        updateSiblingsAfterInsert(
          node.data.nodes,
          newNode.subTree.position,
          nodesToUpdate,
        );

        const updateNodes = Array.from(nodesToUpdate.values());
        nodeMutations.processNodes([
          {
            action: "CREATE",
            nodes: newNode.nodesToRequest,
          },
          {
            action: "UPDATE",
            nodes: [...updateNodes, ...newAncestorsToUpdate],
          },
        ]);
        cb?.(newNode);

        return [...tree];
      }),
    [copiedItem, genNewFolderName, nodeMutations, projectId, setProjectTree],
  );

  const duplicateNode = useCallback(
    (node) =>
      setProjectTree((tree) => {
        if (!node?.data?.id) {
          return tree;
        }
        const newTree = [...tree];

        const { data, parent: parentNode } = node;
        const { folderName } = data;

        const { data: parent } = parentNode;

        if (!parent) {
          return tree;
        }

        const folderData = {};
        if (treeUtils.isAnyFolder(data)) {
          const { data: newFolderName, error } = genNewFolderName(
            folderName,
            parent.nodes,
            false,
          );
          if (error) {
            toast.error(error.message);
            return tree;
          }
          folderData.folderName = newFolderName;
        }

        const newNode = createNodesInput({
          data: {
            ...data,
            ...folderData,
            parentId: treeUtils.validateNodeId(parent.id, projectId),
            position: calculateNewNodePosition(data, parent),
          },
          callback: (nodeData) => {
            if (
              treeUtils.isWizard(nodeData) ||
              treeUtils.isTroxNetcom(nodeData)
            ) {
              const isAnyProduct = treeUtils.isAnyProduct(nodeData);
              const isTroxNetcom = treeUtils.isTroxNetcom(nodeData);
              let kind = isAnyProduct ? NodeKinds.PRODUCT : NodeKinds.FOLDER;
              // Handle TROX Netcom products. Convert to custom product.
              if (isTroxNetcom && isAnyProduct) {
                kind = NodeKinds.CUSTOM_PRODUCT;
              }
              // revalidate the node data with the correct kind to ensure it meets the schema requirements for the new kind
              const parsedNodeData = NodeSchema.safeParse({
                ...nodeData,
                kind,
              });
              if (!parsedNodeData.success) {
                toast.error(parsedNodeData.error.errors[0].message);
                console.error(parsedNodeData.error);
                throw new Error("failed to duplicate node");
              }
              return parsedNodeData.data;
            }
            return nodeData;
          },
        });

        if (newNode.error) {
          toast.error(newNode.error.message);
          return tree;
        }

        if (!Array.isArray(parent.nodes)) {
          parent.nodes = [];
        }

        parent.nodes.push(newNode.subTree);

        nodeMutations.createNodes(newNode.nodesToRequest);

        return newTree;
      }),
    [genNewFolderName, nodeMutations, projectId, setProjectTree],
  );

  const toggleNodeActiveStatus = useCallback(
    (node) => {
      const { data } = node;
      if (
        !(
          treeUtils.isWizardRootFolder(data) ||
          treeUtils.isTroxNetcomFolderRoot(data) ||
          treeUtils.isFolder(data) ||
          treeUtils.isRoot(data) ||
          treeUtils.isCustomProduct(data) ||
          treeUtils.isProduct(data)
        )
      ) {
        return;
      }

      setProjectTree((tree) => {
        const newStatus = !node.data.active;
        const nodesToUpdate = computeNodeToggleActiveStatusUpdates(
          node,
          newStatus,
          projectId,
        );

        nodeMutations.updateNodes(nodesToUpdate);
        // Return a shallow copy so that React-Arborist knows to rerender.
        return [...tree];
      });
      // nodeMutations.updateNodes([])
    },
    [nodeMutations, projectId, setProjectTree],
  );

  return {
    createNode,
    deleteNode,
    updateNode,
    updateProductNodeConfiguration,
    copyNode,
    pasteNode,
    duplicateNode,
    moveNode,
    openNodeDownloadModal,
    toggleNodeActiveStatus,
  };
};
